import React, { useState } from 'react';
import {
  SiFacebook,
  SiInstagram,
  SiTiktok,
  SiYoutube,
  SiX,
  SiPinterest,
  SiThreads,
  SiYelp,
  SiGoogle,
} from '@icons-pack/react-simple-icons';

function LinkedInIcon({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M4.98 3.5C4.98 4.88 3.86 6 2.48 6S0 4.88 0 3.5 1.12 1 2.48 1s2.5 1.12 2.5 2.5zM.5 8h4V24h-4V8zM8 8h3.83v2.19h.05c.53-1.01 1.84-2.19 3.79-2.19C19.2 8 24 10.3 24 16.39V24h-4v-6.69c0-1.6-.03-3.66-2.23-3.66-2.24 0-2.58 1.75-2.58 3.55V24h-4V8z" />
    </svg>
  );
}

/**
 * Per-platform config.
 * brandColor: official Simple Icons hex (or closest equivalent).
 * LinkedIn uses a local SVG since Simple Icons removed it per brand request.
 * Google is used as the closest stand-in for Google Business Profile.
 */
const PLATFORM_CONFIG = {
  facebook:  { Icon: SiFacebook,  label: 'Facebook',               brandColor: '#0866FF' },
  instagram: { Icon: SiInstagram, label: 'Instagram',              brandColor: '#FF0069' },
  tiktok:    { Icon: SiTiktok,    label: 'TikTok',                 brandColor: '#000000' },
  youtube:   { Icon: SiYoutube,   label: 'YouTube',                brandColor: '#FF0000' },
  linkedin:  { Icon: LinkedInIcon, label: 'LinkedIn',              brandColor: '#0A66C2', iconSize: 18 },
  x:         { Icon: SiX,         label: 'X',                      brandColor: '#000000' },
  twitter:   { Icon: SiX,         label: 'X',                      brandColor: '#000000' },
  pinterest: { Icon: SiPinterest, label: 'Pinterest',              brandColor: '#BD081C' },
  threads:   { Icon: SiThreads,   label: 'Threads',                brandColor: '#000000' },
  yelp:      { Icon: SiYelp,      label: 'Yelp',                   brandColor: '#FF1A1A' },
  google:    { Icon: SiGoogle,    label: 'Google Business Profile', brandColor: '#4285F4' },
};

/**
 * All display platforms in canonical order (twitter alias excluded).
 * Import this to render a full set of platform buttons.
 */
export const ALL_PLATFORMS = [
  'facebook',
  'instagram',
  'tiktok',
  'youtube',
  'linkedin',
  'x',
  'pinterest',
  'threads',
  'yelp',
  'google',
];

/**
 * SocialIconButton
 *
 * States:
 *  - base:     white bg, black icon, light gray border
 *  - disabled: base + opacity 0.35 (muted blend-to-white effect), non-interactive
 *  - hover:    brand bg color, white icon (only when href is provided and not disabled)
 *
 * Props:
 *  platform  - one of ALL_PLATFORMS (or 'twitter' alias for x)
 *  href      - full URL; omit or pass null/undefined to render in base state without a link
 *  disabled  - force disabled/muted appearance regardless of href
 *  size      - icon pixel size (default 16)
 */
function SocialIconButton({ platform, href, disabled = false, size = 16, ariaLabel }) {
  const [hovered, setHovered] = useState(false);
  const config = PLATFORM_CONFIG[platform];

  if (!config) return null;

  const { Icon, label, brandColor, iconSize } = config;
  const resolvedSize = iconSize ?? size;
  const isLinked = !disabled && Boolean(href);

  // Derived styles
  const showBrand = hovered && isLinked;
  const bgColor      = showBrand ? brandColor : '#ffffff';
  const iconColor    = showBrand ? '#ffffff'   : '#000000';
  const borderColor  = showBrand ? brandColor  : '#d1d5db';

  const Tag = isLinked ? 'a' : 'span';
  const linkProps = isLinked
    ? { href, target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <Tag
      {...linkProps}
      aria-label={ariaLabel || `Visit on ${label}`}
      title={label}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        border: `1px solid ${borderColor}`,
        borderRadius: 6,
        backgroundColor: bgColor,
        color: iconColor,
        opacity: disabled ? 0.35 : 1,
        cursor: isLinked ? 'pointer' : 'default',
        transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease',
        textDecoration: 'none',
        flexShrink: 0,
        userSelect: 'none',
      }}
    >
      <Icon size={resolvedSize} />
    </Tag>
  );
}

export { PLATFORM_CONFIG };
export default SocialIconButton;
